import React from 'react';
import { resolveTradeSourceLabel } from '../utils/tradeSource';

const PendingOrders = ({ pendingOrders, cancelOrder, loadingOrderId, bare = false }) => {
  const isValidArray = Array.isArray(pendingOrders) && pendingOrders.length > 0;

  const tableHeaderCellClassName = 'border-b widget-divider-border widget-header-fill px-2.5 py-2 text-[10px] font-medium uppercase tracking-[0.06em] text-textSecondary';
  const tableBodyCellClassName = 'border-b widget-divider-border px-2.5 py-2 align-middle text-xs text-textPrimary';
  const dangerButtonClassName = 'inline-flex items-center justify-center rounded-md border border-destructive/30 bg-destructive/10 px-2.5 py-1 text-[11px] font-medium text-destructive transition hover:bg-destructive/15';
  const sourceBadgeClassName = 'inline-flex items-center rounded-full border widget-muted-border bg-backgroundSurface/35 px-2 py-0.5 text-[10px] font-medium text-textSecondary';

  const safeParseFloat = (value, toFixedDigits) => {
    const num = parseFloat(value);
    if (isNaN(num)) return '-';
    return num.toFixed(toFixedDigits);
  };

  const resolveCancelOrderId = (order) => (
    order?.order_id || order?.broker_order_id || order?.ticket || order?.id || null
  );

  const HeaderControls = (
    <div className="inline-flex items-center rounded-full border widget-muted-border bg-backgroundSurface/30 px-3 py-1 text-[11px] font-medium text-textSecondary">
      {isValidArray ? `${pendingOrders.length} orders` : '0 orders'}
    </div>
  );

  const Table = (
    <div className="rounded-xl border widget-muted-border bg-backgroundSurface/20">
      <div className={`overflow-x-auto ${bare ? '' : '-mx-4 sm:mx-0'}`}>
      <table className="w-full min-w-[580px] border-collapse rounded-xl overflow-visible text-xs sm:text-sm">
        <thead>
          <tr>
            <th className={tableHeaderCellClassName}>Ticket</th>
            <th className={tableHeaderCellClassName}>Symbol</th>
            <th className={tableHeaderCellClassName}>Type</th>
            <th className={tableHeaderCellClassName}>Source</th>
            <th className={tableHeaderCellClassName}>Volume</th>
            <th className={tableHeaderCellClassName}>Price</th>
            <th className={tableHeaderCellClassName}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {isValidArray ? (
            pendingOrders.map((order, index) => {
              const cancelOrderId = resolveCancelOrderId(order);
              return (
              <tr key={order.ticket || order.id || index} className="transition hover:bg-secondary/10">
                <td className={`${tableBodyCellClassName} text-center`}>
                  {order.ticket || order.id || '-'}
                </td>
                <td className={`${tableBodyCellClassName} text-center font-medium`}>
                  {order.symbol || '-'}
                </td>
                <td className={`${tableBodyCellClassName} text-center`}>
                  {order.order_type || order.type || '-'}
                </td>
                <td className={`${tableBodyCellClassName} text-center`}>
                  {(() => {
                    const sourceLabel = resolveTradeSourceLabel(order);
                    return sourceLabel === '-'
                      ? <span className="text-textSecondary">-</span>
                      : <span className={sourceBadgeClassName}>{sourceLabel}</span>;
                  })()}
                </td>
                <td className={`${tableBodyCellClassName} text-center font-mono`}>
                  {safeParseFloat(order.volume, 2)}
                </td>
                <td className={`${tableBodyCellClassName} text-center font-mono`}>
                  {safeParseFloat(order.price_open ?? order.price, 5)}
                </td>
                <td className={`${tableBodyCellClassName} text-center`}>
                  <button
                    className={`${dangerButtonClassName} ${
                      loadingOrderId === cancelOrderId ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                    onClick={() => cancelOrder(cancelOrderId)}
                    disabled={!cancelOrderId || loadingOrderId === cancelOrderId}
                    title={cancelOrderId ? 'Cancel pending order' : 'Missing order id'}
                  >
                    {loadingOrderId === cancelOrderId ? '...' : 'Cancel'}
                  </button>
                </td>
              </tr>
              );
            })
          ) : (
            <tr>
              <td
                colSpan="7"
                className="px-3 py-10 text-center text-sm text-textSecondary"
              >
                No pending orders to show.
              </td>
            </tr>
          )}
        </tbody>
      </table>
      </div>
    </div>
  );

  if (bare) {
    return (
      <div className="w-full space-y-3">
        <div className="flex flex-wrap items-center justify-end gap-2">
          {HeaderControls}
        </div>
        {Table}
      </div>
    );
  }

  return (
    <div className="rounded-xl border widget-muted-border bg-backgroundSurface/20 p-4 sm:p-5 mt-4 sm:mt-6">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h3 className="text-base sm:text-lg font-medium text-textPrimary">Pending Orders</h3>
        {HeaderControls}
      </div>
      {Table}
    </div>
  );
};

export default PendingOrders;
